import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { Download, ChevronDown, ChevronUp, CheckCircle2, Loader2, PackageOpen, DollarSign, ShoppingBag, Clock, Truck, Flame } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { useAuth } from "../context/AuthContext";
import { API, formatAUD } from "../lib/shop";

const FILTERS = [
  { key: "", label: "All" },
  { key: "placed", label: "Placed" },
  { key: "fulfilled", label: "Fulfilled" },
];

const OrderRow = ({ order, onFulfil }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const a = order.address;

  const fulfil = async () => {
    setBusy(true);
    await onFulfil(order.id);
    setBusy(false);
  };

  return (
    <>
      <TableRow className="hover:bg-muted/60" data-testid={`admin-order-row-${order.order_number}`}>
        <TableCell className="font-mono text-xs font-semibold">{order.order_number}</TableCell>
        <TableCell className="text-xs text-muted-foreground">{order.created_at.slice(0, 16).replace("T", " ")}</TableCell>
        <TableCell>
          <div className="text-sm font-medium">{order.customer_name}</div>
          <div className="text-xs text-muted-foreground">{order.customer_email}</div>
        </TableCell>
        <TableCell className="text-xs text-muted-foreground">{a.suburb} {a.state} {a.postcode}</TableCell>
        <TableCell className="text-right font-semibold tabular-nums">{formatAUD(order.total)}</TableCell>
        <TableCell>
          <Badge
            data-testid={`admin-order-status-${order.order_number}`}
            className={`rounded-sm ${order.status === "fulfilled" ? "bg-[#16a34a] hover:bg-[#16a34a]" : order.status === "pending_payment" ? "bg-muted text-muted-foreground hover:bg-muted" : "bg-accent text-accent-foreground hover:bg-accent"}`}
          >
            {order.status === "pending_payment" ? "awaiting payment" : order.status}
          </Badge>
          {order.payment_status === "paid" && (
            <div className="mt-1 text-[10px] font-bold uppercase tracking-wider text-[#16a34a]">Paid</div>
          )}
        </TableCell>
        <TableCell>
          <div className="flex items-center justify-end gap-2">
            {order.status === "placed" && (
              <button
                onClick={fulfil}
                disabled={busy}
                data-testid={`admin-fulfil-button-${order.order_number}`}
                className="flex items-center gap-1 rounded-sm bg-primary px-2.5 py-1.5 text-xs font-semibold text-primary-foreground transition-colors hover:bg-secondary disabled:opacity-60"
              >
                {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />} Fulfil
              </button>
            )}
            <button
              onClick={() => setOpen((o) => !o)}
              data-testid={`admin-order-expand-${order.order_number}`}
              className="rounded-sm border border-border p-1.5 text-muted-foreground transition-colors hover:bg-muted"
              aria-label="Toggle order details"
            >
              {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
          </div>
        </TableCell>
      </TableRow>
      {open && (
        <TableRow className="bg-muted/40" data-testid={`admin-order-detail-${order.order_number}`}>
          <TableCell colSpan={7} className="px-6 py-4">
            <div className="text-xs text-muted-foreground">
              Deliver to: {order.customer_name}, {a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.suburb} {a.state} {a.postcode}
              {order.customer_phone ? ` · ${order.customer_phone}` : ""}
            </div>
            <ul className="mt-3 space-y-1.5">
              {order.items.map((i, idx) => (
                <li key={idx} className="flex justify-between gap-4 text-sm">
                  <span>
                    <span className="font-mono text-xs">{i.product_code}</span> — {i.product_name} ({i.description}
                    {i.insulation_colour ? `, ${i.insulation_colour}` : ""}) × {i.quantity}
                  </span>
                  <span className="tabular-nums font-medium">{formatAUD(i.subtotal)}</span>
                </li>
              ))}
            </ul>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const StatCard = ({ icon: Icon, label, value, accent, testId }) => (
  <div className="rounded-sm border border-border bg-white p-5" data-testid={testId}>
    <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
      <Icon className={`h-4 w-4 ${accent}`} /> {label}
    </div>
    <div className="mt-2 text-2xl font-bold tabular-nums text-primary">{value}</div>
  </div>
);

const SalesSnapshot = ({ stats }) => {
  if (!stats) {
    return (
      <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-24 animate-pulse rounded-sm border border-border bg-muted" />
        ))}
      </div>
    );
  }
  return (
    <div className="mt-8" data-testid="sales-snapshot">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard icon={DollarSign} label="Revenue (paid)" value={formatAUD(stats.total_revenue)} accent="text-[#16a34a]" testId="stat-revenue" />
        <StatCard icon={ShoppingBag} label="Paid orders" value={stats.paid_orders} accent="text-secondary" testId="stat-paid-orders" />
        <StatCard icon={Truck} label="Fulfilled" value={stats.fulfilled_orders} accent="text-primary" testId="stat-fulfilled" />
        <StatCard icon={Clock} label="Awaiting payment" value={stats.awaiting_payment} accent="text-[#dfa557]" testId="stat-awaiting" />
      </div>
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        {stats.top_variants.length > 0 && (
          <div className="rounded-sm border border-border bg-white p-5" data-testid="best-sellers">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Best-selling variants (all time)</h2>
            <ul className="mt-3 divide-y divide-border">
              {stats.top_variants.map((v, idx) => (
                <li key={v.product_code} className="flex items-center justify-between gap-4 py-2 text-sm" data-testid={`best-seller-${idx + 1}`}>
                  <span className="flex items-center gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-sm bg-primary text-xs font-bold text-primary-foreground">{idx + 1}</span>
                    <span>
                      <span className="font-mono text-xs font-semibold">{v.product_code}</span>
                      <span className="ml-2 text-muted-foreground">{v.description}{v.insulation_colour ? `, ${v.insulation_colour}` : ""}</span>
                    </span>
                  </span>
                  <span className="shrink-0 text-right">
                    <span className="font-semibold tabular-nums">{formatAUD(v.revenue)}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{v.qty_sold} pack{v.qty_sold > 1 ? "s" : ""}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
        {stats.fast_movers?.length > 0 && (
          <div className="rounded-sm border border-border bg-white p-5" data-testid="restock-radar">
            <h2 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <Flame className="h-3.5 w-3.5 text-[#dfa557]" /> Restock radar — fastest sellers (last 30 days)
            </h2>
            <ul className="mt-3 divide-y divide-border">
              {stats.fast_movers.map((v, idx) => (
                <li key={v.product_code} className="flex items-center justify-between gap-4 py-2 text-sm" data-testid={`fast-mover-${idx + 1}`}>
                  <span className="flex items-center gap-3">
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-sm bg-[#dfa557] text-xs font-bold text-primary">{idx + 1}</span>
                    <span>
                      <span className="font-mono text-xs font-semibold">{v.product_code}</span>
                      <span className="ml-2 text-muted-foreground">{v.description}{v.insulation_colour ? `, ${v.insulation_colour}` : ""}</span>
                    </span>
                  </span>
                  <span className="shrink-0 text-right">
                    <span className="font-semibold tabular-nums">{v.qty_30d} pack{v.qty_30d > 1 ? "s" : ""}</span>
                    <span className="ml-2 text-xs text-muted-foreground">~{v.packs_per_week}/wk</span>
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-3 text-xs text-muted-foreground">Keep an eye on these — they're moving fastest and may need restocking first.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default function AdminPage() {
  const { user } = useAuth();
  const [filter, setFilter] = useState("");
  const [orders, setOrders] = useState(null);
  const [stats, setStats] = useState(null);
  const [loadError, setLoadError] = useState(false);

  const loadStats = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/stats`);
      setStats(data);
    } catch {
      /* stats are non-blocking */
    }
  }, []);

  const load = useCallback(async (f) => {
    const { data } = await axios.get(`${API}/admin/orders`, { params: f ? { status: f } : {} });
    setOrders(data);
    setLoadError(false);
  }, []);

  useEffect(() => {
    if (user && user.role === "admin") loadStats();
  }, [user, loadStats]);

  useEffect(() => {
    if (user && user.role === "admin")
      load(filter).catch(() => {
        setLoadError(true);
        setOrders([]);
        toast.error("Failed to load orders");
      });
  }, [user, filter, load]);

  if (user === undefined) return <div className="py-24 text-center"><Loader2 className="mx-auto h-8 w-8 animate-spin text-primary" /></div>;
  if (user === null) return <Navigate to="/login" replace />;
  if (user.role !== "admin") {
    return (
      <div className="mx-auto max-w-md px-4 py-24 text-center" data-testid="admin-forbidden">
        <p className="text-lg font-semibold">Admin access required</p>
        <p className="mt-2 text-sm text-muted-foreground">This area is reserved for the store owner.</p>
      </div>
    );
  }

  const fulfil = async (orderId) => {
    try {
      await axios.patch(`${API}/admin/orders/${orderId}/status`, { status: "fulfilled" });
      toast.success("Order marked as fulfilled");
      await load(filter);
      loadStats();
    } catch {
      toast.error("Could not update order");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/orders/export`, {
        params: filter ? { status: filter } : {},
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "coreconnect-orders.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 md:px-8" data-testid="admin-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Orders</h1>
          <p className="mt-1 text-sm text-muted-foreground">Manage incoming orders and fulfilment.</p>
        </div>
        <button
          onClick={exportCsv}
          data-testid="admin-export-csv-button"
          className="flex items-center gap-2 rounded-sm border border-primary px-4 py-2.5 text-sm font-semibold text-primary transition-colors hover:bg-primary hover:text-primary-foreground"
        >
          <Download className="h-4 w-4" /> Export CSV
        </button>
      </div>

      <SalesSnapshot stats={stats} />

      <div className="mt-6 flex gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            data-testid={`admin-filter-${f.label.toLowerCase()}`}
            className={`rounded-sm px-4 py-2 text-sm font-medium transition-colors ${
              filter === f.key ? "bg-primary text-primary-foreground" : "border border-border bg-white text-muted-foreground hover:text-primary"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="mt-6 overflow-x-auto rounded-sm border border-border bg-white">
        {orders === null ? (
          <div className="py-16 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin text-primary" /></div>
        ) : loadError ? (
          <div className="py-16 text-center text-sm text-destructive" data-testid="admin-load-error">
            Could not load orders. Check your connection and refresh the page.
          </div>
        ) : orders.length === 0 ? (
          <div className="py-16 text-center text-sm text-muted-foreground" data-testid="admin-no-orders">
            <PackageOpen className="mx-auto mb-3 h-8 w-8 text-muted-foreground/40" />
            No orders {filter ? `with status "${filter}"` : "yet"}.
          </div>
        ) : (
          <Table>
            <TableHeader className="bg-muted">
              <TableRow>
                <TableHead>Order</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Destination</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-40 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((o) => <OrderRow key={o.id} order={o} onFulfil={fulfil} />)}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
